
import React, { useEffect, lazy, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { buildNav } from "./navigation/navConfig";

// Pages. Login stays in the main bundle (instant first paint); everything else
// is lazy-loaded so the initial download is small — critical on ship wifi and
// phones. The PWA service worker precaches every chunk, so offline still works.
import LoginPage from "./pages/Login.jsx";
import ChunkErrorBoundary from "./components/ChunkErrorBoundary.jsx";
import FeedbackWidget from "./components/FeedbackWidget.jsx";
// "Choose a Workspace" (RoleHub) retired 2026-07-11 (Jill): every recognized
// role lands directly on its home tool; /hub now only catches a login the
// portal doesn't recognize.
function NoAccessScreen() {
  return (
    <div className="card" style={{ margin: "10vh auto", textAlign: "center" }}>
      <h2>Account setup incomplete</h2>
      <p style={{ color: "var(--text-muted, #6b7280)" }}>
        Your login works, but no role is set up for it yet. Contact the office
        to finish your account, then sign in again.
      </p>
    </div>
  );
}
const SystemAdminDashboard = lazy(() => import("./pages/SystemAdminDashboard"));
const AdminPanel = lazy(() => import("./pages/AdminPanel.jsx"));
const ReservationsUpload = lazy(() => import("./pages/ReservationsUpload.jsx"));
const OfficeHome = lazy(() => import("./pages/OfficeHome.jsx"));
const ContactDirectory = lazy(() => import("./pages/ContactDirectory.jsx"));
const DispatchUpload = lazy(() => import("./pages/DispatchUpload.jsx"));
const WinterRosterUpload = lazy(() => import("./pages/WinterRosterUpload.jsx"));
const CalendarEdits = lazy(() => import("./pages/CalendarEdits.jsx"));
const PilotBoatScheduleUpload = lazy(() => import("./pages/PilotBoatScheduleUpload.jsx"));
const PilotSlip = lazy(() => import("./pages/PilotSlip.jsx"));
const PilotSlipEditor = lazy(() => import("./pages/PilotSlipEditor.jsx"));
const PilotProfileEditor = lazy(() => import("./pages/PilotProfileEditor.jsx"));
const PilotRequests = lazy(() => import("./pages/PilotRequests.jsx"));
const TrainingRecords = lazy(() => import("./pages/TrainingRecords.jsx"));
const TrainingRecordEditor = lazy(() => import("./pages/TrainingRecordEditor.jsx"));
const TrainingGradingQueue = lazy(() => import("./pages/TrainingGradingQueue.jsx"));
const TraineeTripPlanner = lazy(() => import("./pages/TraineeTripPlanner.jsx"));
const TrainingChairDashboard = lazy(() => import("./pages/TrainingChairDashboard.jsx"));
const TrainingRequirements = lazy(() => import("./pages/TrainingRequirements.jsx"));
const TrainingQuarterly = lazy(() => import("./pages/TrainingQuarterly.jsx"));
const MentorReview = lazy(() => import("./pages/MentorReview.jsx"));
const CommitteeReview = lazy(() => import("./pages/CommitteeReview.jsx"));
const VesselSlipHistory = lazy(() => import("./pages/VesselSlipHistory.jsx"));
const ExpenseReportEditor = lazy(() => import("./pages/ExpenseReportEditor"));
const ContinuingEducationLanding = lazy(() => import("./pages/ContinuingEducationLanding"));
const CeCourseForm = lazy(() => import("./pages/CeCourseForm"));
const ManageCeCourses = lazy(() => import("./pages/ManageCeCourses"));
const CeAdminAudits = lazy(() => import("./pages/CeAdminAudits"));
const CeObserverForm = lazy(() => import("./pages/CeObserverForm.jsx"));
const SeasonSlips = lazy(() => import("./pages/SeasonSlips.jsx"));
const VesselManager = lazy(() => import("./pages/VesselManager.jsx"));
const UserManager = lazy(() => import("./pages/UserManager.jsx"));
const SetPassword = lazy(() => import("./pages/SetPassword"));
const VesselMovementDiff = lazy(() => import("./components/admin/vessel-movement-diff/VesselMovementDiff.jsx"));
const VoyagePlan = lazy(() => import("./pages/VoyagePlan"));
const TripWorkspace = lazy(() => import("./pages/TripWorkspace.jsx"));
const TraineeOpportunities = lazy(() => import("./pages/TraineeOpportunities.jsx"));
const ClaaTraineeQueue = lazy(() => import("./pages/ClaaTraineeQueue.jsx"));
const PilotRoster = lazy(() => import("./pages/PilotRoster.jsx"));
const DispatchCalendar = lazy(() => import("./pages/DispatchCalendar.jsx"));
const TermsAndPrivacy = lazy(() => import("./pages/TermsAndPrivacy.jsx"));
const AskTheBoard = lazy(() => import("./pages/AskTheBoard.jsx"));
const AskRulebook = lazy(() => import("./pages/AskRulebook.jsx"));
const BoardReports = lazy(() => import("./pages/BoardReports.jsx"));
const BoardQueue = lazy(() => import("./pages/BoardQueue.jsx"));
const BodRequests = lazy(() => import("./pages/BodRequests.jsx"));
const BoardWorkspace = lazy(() => import("./pages/BoardWorkspace.jsx"));
const MeetingFinder = lazy(() => import("./pages/MeetingFinder.jsx"));
const TrainingDigest = lazy(() => import("./pages/TrainingDigest.jsx"));
const SubmittedPaperwork = lazy(() => import("./pages/SubmittedPaperwork.jsx"));
const SeaService = lazy(() => import("./pages/SeaService.jsx"));
const SeaServiceReview = lazy(() => import("./pages/SeaServiceReview.jsx"));
const CargoPool = lazy(() => import("./pages/CargoPool.jsx"));
const WhatsNew = lazy(() => import("./pages/WhatsNew.jsx"));
const HelpVideos = lazy(() => import("./pages/HelpVideos.jsx"));
const HelpGuides = lazy(() => import("./pages/HelpGuides.jsx"));
const PostToFleet = lazy(() => import("./pages/PostToFleet.jsx"));

// Components
import AuthenticatedLayout from "./components/AuthenticatedLayout";
import ProtectedRoute from "./components/ProtectedRoute.jsx";
import UpdateBanner from "./components/UpdateBanner.jsx";
// Eager, not lazy: Support must open when a lazy chunk cannot (see the page).
import PortalSupport from "./pages/PortalSupport.jsx";
// Eager for the same reason: it is the screen old home screen copies land on.
import { PortalMovedScreen } from "./components/PortalMoved.jsx";
import { onRetiredAddress } from "./lib/portalMove";

// Resume-where-you-left-off: remember the last screen (path + query, so e.g.
// the trip workspace comes back on the same trip and tab) and land there when
// the app reopens at "/". Kept for 48h — a fresh mid-trip reopen resumes; a
// reopen days later starts clean at the dashboard.
const LAST_ROUTE_KEY = "lastRoute";
const LAST_ROUTE_MAX_AGE_MS = 48 * 60 * 60 * 1000;
// /support too: its "Back to the portal" goes to "/", which would resume
// straight back onto Support.
const NON_RESUMABLE_PATHS = ["/", "/login", "/set-password", "/support"];

function LastRouteTracker() {
  const location = useLocation();
  useEffect(() => {
    if (NON_RESUMABLE_PATHS.includes(location.pathname)) return;
    try {
      localStorage.setItem(
        LAST_ROUTE_KEY,
        JSON.stringify({ path: location.pathname + location.search, at: Date.now() })
      );
    } catch { /* storage full/blocked — resume is best-effort */ }
  }, [location]);
  return null;
}

function readLastRoute() {
  try {
    const raw = localStorage.getItem(LAST_ROUTE_KEY);
    if (!raw) return null;
    const { path, at } = JSON.parse(raw);
    if (typeof path !== "string" || !path.startsWith("/")) return null;
    if (NON_RESUMABLE_PATHS.includes(path)) return null;
    if (Date.now() - (at || 0) > LAST_ROUTE_MAX_AGE_MS) return null;
    return path;
  } catch {
    return null;
  }
}

// See the catch-all route below. The clear runs in an effect, which React
// fires AFTER LastRouteTracker's save of this same unknown path — so the
// removal wins and the next "/" starts clean.
function UnknownRouteHome() {
  useEffect(() => {
    try { localStorage.removeItem(LAST_ROUTE_KEY); } catch { /* ignore */ }
  }, []);
  return <Navigate to="/" replace />;
}

// At the retired address the app opens on "The portal has moved" and nothing
// else: a link to seapaportal.com, no login, no way back into the old app
// (Jill 2026-10-30: the season is over, nobody is mid-slip on the old
// address). Drafts are still copied to the account by the screen itself.
function AppBody() {
  if (!onRetiredAddress()) return <AppRoutes />;
  return <PortalMovedScreen />;
}

export default function App() {
  return (
    <Router>
      <AuthProvider>
        <LastRouteTracker />
        <AppBody />
        <UpdateBanner />
      </AuthProvider>
    </Router>
  );
}

// The Board of Directors = President, VP, Secretary, Board Member only.
// (Training Chair and CE Lead are committee roles, NOT board members.)
const BOARD_POSITIONS = ["president", "vice_president", "secretary", "member"];
import { MEETING_FINDER_POSITIONS } from "./lib/meetingFinder";

function AppRoutes() {
  const { session, roles, rolesLoaded, boardPosition, committeePositions, signOut } = useAuth();

  // Root Redirect: straight to work — resume where you left off, else your
  // primary tool (My Schedule for pilots, Training Opportunities for trainees, Roster
  // for office, the queue for agents). No hub, no dashboard pages.
  const RootRedirect = () => {
    if (!session) return <Navigate to="/login" replace />;

    const last = readLastRoute();
    if (last) return <Navigate to={last} replace />;

    // Roles still in flight (fresh install has no cached copy) — wait rather
    // than guess: an empty list reads as "no recognized role" and dumps the
    // user on the workspace hub (Jill's iPad, 2026-07-11).
    if (!rolesLoaded) {
      return (
        <div style={{ display: "flex", justifyContent: "center", padding: "60px 0", color: "#6b7280" }}>
          Loading…
        </div>
      );
    }

    const { homePath } = buildNav({ roles, boardPosition, committeePositions });
    return <Navigate to={homePath} replace />;
  };

  return (
    <div style={{ overflowX: "visible", width: "100%" }}>
      <ChunkErrorBoundary>
      <Suspense
        fallback={
          <div style={{ display: "flex", justifyContent: "center", padding: "60px 0", color: "#6b7280" }}>
            Loading…
          </div>
        }
      >
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/set-password" element={<SetPassword />} />
        {/* No login: readable when signing in is the thing that is broken */}
        <Route path="/support" element={<PortalSupport />} />

        {/* Root Redirect */}
        <Route path="/" element={<RootRedirect />} />

        {/* Authenticated Hub */}
        <Route path="/hub" element={
          session ? <NoAccessScreen /> : <Navigate to="/login" replace />
        } />

        {/* Protected Routes */}
        {/* Helper to wrap layout */}
        <Route element={<AuthenticatedLayout onLogout={signOut} userRoles={roles} />}>

          <Route path="/system-admin" element={
            <ProtectedRoute allowedRoles={["system_admin"]}>
              <SystemAdminDashboard />
            </ProtectedRoute>
          } />

          <Route path="/admin" element={
            <ProtectedRoute allowedRoles={["system_admin"]} allowedPositions={["president", "vice_president", "secretary"]}>
              <AdminPanel />
            </ProtectedRoute>
          } />

          {/* Retired dashboard pages ("one person, one home", 2026-07-02) —
              old links and bookmarks land back at your primary tool */}
          {/* The office's card home (Jill 2026-09-22). scheduler is the
              dispatch-sheet publisher role (Jessie, pilots@). */}
          <Route path="/office" element={
            <ProtectedRoute allowedRoles={["system_admin", "office", "reservations", "accounting", "scheduler"]}>
              <OfficeHome />
            </ProtectedRoute>
          } />
          {/* Addresses, phones, emergency contacts (Jill + Jessie 2026-09-26).
              pilot_contacts RLS answers the same four roles only. */}
          <Route path="/contact-directory" element={
            <ProtectedRoute allowedRoles={["system_admin", "office", "accounting", "dispatch"]}>
              <ContactDirectory />
            </ProtectedRoute>
          } />
          <Route path="/bod" element={<Navigate to="/" replace />} />

          <Route path="/reservations-upload" element={
            <ProtectedRoute allowedRoles={["system_admin", "reservations"]}>
              <ReservationsUpload />
            </ProtectedRoute>
          } />

          {/* Publish dispatch — President/VP (position), Jessie (scheduler), system admin */}
          <Route path="/dispatch-upload" element={
            <ProtectedRoute allowedRoles={["system_admin", "scheduler"]} allowedPositions={["president", "vice_president"]}>
              <DispatchUpload />
            </ProtectedRoute>
          } />

          {/* Winter duty roster (on-dispatch weeks) — same hands as the dispatch publisher */}
          <Route path="/winter-roster-upload" element={
            <ProtectedRoute allowedRoles={["system_admin", "scheduler"]} allowedPositions={["president", "vice_president"]}>
              <WinterRosterUpload />
            </ProtectedRoute>
          } />

          {/* CLAA pilot boat schedule — same hands as the dispatch publisher */}
          <Route path="/pilot-boat-schedule-upload" element={
            <ProtectedRoute allowedRoles={["system_admin", "scheduler"]} allowedPositions={["president", "vice_president"]}>
              <PilotBoatScheduleUpload />
            </ProtectedRoute>
          } />

          {/* Ship calendar one-off edits — same hands as the dispatch publisher */}
          <Route path="/calendar-edits" element={
            <ProtectedRoute allowedRoles={["system_admin", "scheduler"]} allowedPositions={["president", "vice_president"]}>
              <CalendarEdits />
            </ProtectedRoute>
          } />

          {/* Retired 2026-09-22 (Jill's office cleanup): /dispatch-schedule and
              /dispatch-matrix (last season's assignment system), /equity-dashboard
              (read static JSON files that no longer exist), /pilot-assignments
              (a placeholder never built) and /expense-approvals (the office keeps
              expense tracking in its own tool). */}

          <Route path="/pilot" element={<Navigate to="/" replace />} />

          {/* My Travel retired 2026-07-02 — personal travel lives in My
              Schedule; Who's Traveling is a Dispatch tab */}
          <Route path="/my-travel" element={<Navigate to="/dispatch-calendar" replace />} />

          <Route path="/trainee" element={<Navigate to="/" replace />} />
          <Route path="/agent" element={<Navigate to="/" replace />} />

          <Route path="/training-opportunities" element={
            <ProtectedRoute allowedRoles={["trainee", "pilot", "system_admin", "office", "accounting"]}>
              <TraineeOpportunities />
            </ProtectedRoute>
          } />

          <Route path="/claa-trainee-queue" element={
            <ProtectedRoute allowedRoles={["agent", "pilot", "system_admin", "office", "accounting"]}>
              <ClaaTraineeQueue />
            </ProtectedRoute>
          } />

          <Route path="/pilot-roster" element={
            <ProtectedRoute allowedRoles={["system_admin", "office", "accounting"]}>
              <PilotRoster />
            </ProtectedRoute>
          } />

          <Route path="/dispatch-calendar" element={
            <ProtectedRoute allowedRoles={["pilot", "trainee", "system_admin", "office", "accounting"]}>
              <DispatchCalendar />
            </ProtectedRoute>
          } />

          <Route path="/ask-board" element={
            <ProtectedRoute allowedRoles={["pilot", "system_admin"]}>
              <AskTheBoard />
            </ProtectedRoute>
          } />

          {/* Ask (the rules): document Q&A for everyone at SEAPA. The agent
              (CLAA) role is deliberately absent — the edge function refuses
              them server-side too. */}
          <Route path="/ask-rules" element={
            <ProtectedRoute allowedRoles={["pilot", "trainee", "office", "accounting", "system_admin"]}>
              <AskRulebook />
            </ProtectedRoute>
          } />

          <Route path="/board-queue" element={
            <ProtectedRoute allowedRoles={["pilot", "system_admin"]}>
              <BoardQueue />
            </ProtectedRoute>
          } />

          {/* Board Reports: BOD seats only (plus admin). The paperwork ledger
              behind it is gated server-side too (board_paperwork_rows RPC). */}
          <Route path="/board-reports" element={
            <ProtectedRoute allowedRoles={["system_admin"]} allowedPositions={["president", "vice_president", "secretary", "member"]}>
              <BoardReports />
            </ProtectedRoute>
          } />

          {/* Travel Sheet became a Dispatch tab (Jill 2026-08-19 — it was too
              hidden in More). Old links and bookmarks land on that tab. */}
          <Route path="/travel-sheet" element={<Navigate to="/dispatch-calendar?view=travelsheet" replace />} />

          <Route path="/bod-requests" element={
            <ProtectedRoute allowedRoles={["system_admin"]} allowedPositions={BOARD_POSITIONS}>
              <BodRequests />
            </ProtectedRoute>
          } />

          {/* Board workspace: BOD members and admins only. Office logins were
              dropped 2026-09-14 (Jill: the board never delegates to the office). */}
          <Route path="/board-workspace" element={
            <ProtectedRoute allowedRoles={["system_admin"]} allowedPositions={BOARD_POSITIONS}>
              <BoardWorkspace />
            </ProtectedRoute>
          } />

          {/* Meeting Finder (Aaron via Jill 2026-09-07): board seats and
              committee positions only, never the whole fleet. The position
              list lives with the finder's group presets so a new committee
              is one edit. */}
          <Route path="/meeting-finder" element={
            <ProtectedRoute allowedRoles={["system_admin"]} allowedPositions={MEETING_FINDER_POSITIONS}>
              <MeetingFinder />
            </ProtectedRoute>
          } />

          {/* CE admin — Training Chair, CE Lead, and CE Organizer positions */}
          <Route path="/manage-ce-courses" element={
            <ProtectedRoute allowedRoles={["system_admin"]} allowedPositions={["training_chair", "ce_lead", "ce_organizer"]}>
              <ManageCeCourses />
            </ProtectedRoute>
          } />

          <Route path="/ce-audits" element={
            <ProtectedRoute allowedRoles={["system_admin"]} allowedPositions={["training_chair", "ce_lead", "ce_organizer"]}>
              <CeAdminAudits />
            </ProtectedRoute>
          } />

          {/* Seniority folded into the Trainee Audit (2026-07-07) */}
          <Route path="/trainee-seniority" element={<Navigate to="/training-chair" replace />} />

          <Route path="/trainee-digest" element={
            <ProtectedRoute allowedRoles={["system_admin"]} allowedPositions={["training_chair"]}>
              <TrainingDigest />
            </ProtectedRoute>
          } />

          {/* trainees lost the Trip Workspace 2026-11-03 (Jill) */}
          <Route path="/trip-workspace" element={
            <ProtectedRoute allowedRoles={["pilot", "system_admin"]}>
              <TripWorkspace />
            </ProtectedRoute>
          } />

          {/* Cargo Training Pool: full transparency — every pilot/trainee can
              view; pool members request; the Training Chair oversees. */}
          <Route path="/cargo-pool" element={
            <ProtectedRoute allowedRoles={["pilot", "trainee", "system_admin"]}>
              <CargoPool />
            </ProtectedRoute>
          } />

          {/* slip_coordinator is a roster POSITION now, not a user_roles role
              (Jill 2026-08-21) — pilot_positions via the Pilot Roster. */}
          <Route path="/pilot-slip" element={
            <ProtectedRoute allowedRoles={["pilot", "system_admin"]} allowedPositions={["slip_coordinator"]}>
              <PilotSlip />
            </ProtectedRoute>
          } />

          <Route path="/pilot-slip-editor" element={
            <ProtectedRoute allowedRoles={["pilot", "system_admin"]} allowedPositions={["slip_coordinator"]}>
              <PilotSlipEditor />
            </ProtectedRoute>
          } />

          <Route path="/pilot-profile" element={
            <ProtectedRoute allowedRoles={["pilot", "system_admin"]}>
              <PilotProfileEditor />
            </ProtectedRoute>
          } />

          <Route path="/pilot-requests" element={
            <ProtectedRoute allowedRoles={["pilot", "system_admin"]}>
              <PilotRequests />
            </ProtectedRoute>
          } />

          <Route path="/profile" element={
            <ProtectedRoute allowedRoles={["pilot", "system_admin", "trainee"]}>
              <PilotProfileEditor />
            </ProtectedRoute>
          } />

          {/* Update Users — compose What's New notes without a deploy */}
          <Route path="/post-to-fleet" element={
            <ProtectedRoute allowedRoles={["system_admin"]}>
              <PostToFleet />
            </ProtectedRoute>
          } />

          {/* What's New — the running announcements tally; every authenticated
              role can read it (each entry's audience filters what appears) */}
          <Route path="/whats-new" element={
            <ProtectedRoute allowedRoles={["pilot", "trainee", "system_admin", "office", "accounting", "dispatch", "agent"]}>
              <WhatsNew />
            </ProtectedRoute>
          } />
          {/* the office is not on the list: no help videos reach it (beta silo) */}
          {/* the step-by-step guides; the office and the CLAA agent have none (Jill 2026-11-03) */}
          <Route path="/help" element={
            <ProtectedRoute allowedRoles={["pilot", "trainee", "system_admin"]}>
              <HelpGuides />
            </ProtectedRoute>
          } />
          <Route path="/help/:guide" element={
            <ProtectedRoute allowedRoles={["pilot", "trainee", "system_admin"]}>
              <HelpGuides />
            </ProtectedRoute>
          } />
          <Route path="/help-videos" element={
            <ProtectedRoute allowedRoles={["pilot", "trainee", "agent", "system_admin"]}>
              <HelpVideos />
            </ProtectedRoute>
          } />

          {/* Terms of Use + Privacy Notice — every authenticated role can read it */}
          <Route path="/terms" element={
            <ProtectedRoute allowedRoles={["pilot", "trainee", "system_admin", "office", "accounting", "dispatch", "agent"]}>
              <TermsAndPrivacy />
            </ProtectedRoute>
          } />

          {/* Training Records v2 (2026-07-07): trainee/deputy home, the TR
              form, and the training pilot's grading queue */}
          <Route path="/training-records" element={
            <ProtectedRoute allowedRoles={["pilot", "system_admin", "trainee"]}>
              <TrainingRecords />
            </ProtectedRoute>
          } />
          <Route path="/training-record" element={
            <ProtectedRoute allowedRoles={["pilot", "system_admin", "trainee"]}>
              <TrainingRecordEditor />
            </ProtectedRoute>
          } />
          <Route path="/tr-grading" element={
            <ProtectedRoute allowedRoles={["pilot", "system_admin"]}>
              <TrainingGradingQueue />
            </ProtectedRoute>
          } />
          <Route path="/trainee-trip" element={
            <ProtectedRoute allowedRoles={["pilot", "system_admin", "trainee"]}>
              <TraineeTripPlanner />
            </ProtectedRoute>
          } />
          <Route path="/training-chair" element={
            <ProtectedRoute allowedRoles={["system_admin"]} allowedPositions={["training_chair"]}>
              <TrainingChairDashboard />
            </ProtectedRoute>
          } />
          <Route path="/training-requirements" element={
            <ProtectedRoute allowedRoles={["system_admin"]} allowedPositions={["training_chair"]}>
              <TrainingRequirements />
            </ProtectedRoute>
          } />
          <Route path="/training-quarterly" element={
            <ProtectedRoute allowedRoles={["system_admin"]} allowedPositions={["training_chair"]}>
              <TrainingQuarterly />
            </ProtectedRoute>
          } />
          <Route path="/mentor-review" element={
            <ProtectedRoute allowedRoles={["pilot", "system_admin"]}>
              <MentorReview />
            </ProtectedRoute>
          } />
          {/* like /mentor-review: any pilot may open it, the page itself
              explains when no committee position is held and the RPC enforces
              who can actually record anything */}
          <Route path="/committee-review" element={
            <ProtectedRoute allowedRoles={["pilot", "system_admin"]}>
              <CommitteeReview />
            </ProtectedRoute>
          } />
          <Route path="/vessel-slips" element={
            <ProtectedRoute allowedRoles={["pilot", "system_admin", "trainee"]}>
              <VesselSlipHistory />
            </ProtectedRoute>
          } />
          {/* v1 prototypes retired in favor of the pages above */}
          <Route path="/progress-tracker" element={<Navigate to="/training-records" replace />} />
          {/* the bulk backfill page retired 2026-10-08; Add to Tracker on the Records tab replaces it */}
          <Route path="/tr-backfill" element={<Navigate to="/training-records" replace />} />
          <Route path="/training-dashboard" element={<Navigate to="/tr-grading" replace />} />

          <Route path="/paperwork" element={
            <ProtectedRoute allowedRoles={["pilot", "system_admin"]}>
              <SubmittedPaperwork />
            </ProtectedRoute>
          } />

          {/* Sea service letters: trainees and deputies accumulate qualifying
              days too, so they get the page alongside full pilots */}
          <Route path="/sea-service" element={
            <ProtectedRoute allowedRoles={["pilot", "trainee", "system_admin"]}>
              <SeaService />
            </ProtectedRoute>
          } />
          <Route path="/sea-service-review" element={
            <ProtectedRoute allowedRoles={["system_admin"]} allowedPositions={["training_chair"]}>
              <SeaServiceReview />
            </ProtectedRoute>
          } />

          {/* The standalone Expenses manager folded into Paperwork (2026-07-02) */}
          <Route path="/expense-reports" element={<Navigate to="/paperwork" replace />} />
          <Route path="/expense-report-editor" element={
            <ProtectedRoute allowedRoles={['pilot', 'system_admin']}>
              <ExpenseReportEditor />
            </ProtectedRoute>
          } />

          <Route path="/continuing-education" element={
            <ProtectedRoute allowedRoles={['pilot', 'system_admin']}>
              <ContinuingEducationLanding />
            </ProtectedRoute>
          } />
          <Route path="/ce-course-form" element={
            <ProtectedRoute allowedRoles={['pilot', 'system_admin']}>
              <CeCourseForm />
            </ProtectedRoute>
          } />
          <Route path="/ce-observer-form" element={
            <ProtectedRoute allowedRoles={['pilot', 'system_admin']}>
              <CeObserverForm />
            </ProtectedRoute>
          } />

          {/* Season Slips 2027: the slip coordinators' (Tyler, Nathan) review
              page for the generic season slips built by scripts/batch_slips_2027.
              Verdicts, answers and downloads (Jill 2026-09-21). The old Batch
              Slip Generator page and its edge function were removed the same
              day; the /batch-slips address stays for bookmarks. */}
          <Route path="/batch-slips" element={
            <ProtectedRoute allowedRoles={["system_admin"]} allowedPositions={["slip_coordinator"]}>
              <SeasonSlips />
            </ProtectedRoute>
          } />

          {/* Office lost this tab 2026-09-22 (Jill): it is the retired CSV
              diff tool, not vessel reference data. Admin only now. */}
          <Route path="/vessels" element={
            <ProtectedRoute allowedRoles={["system_admin"]}>
              <VesselManager />
            </ProtectedRoute>
          } />

          <Route path="/users" element={
            <ProtectedRoute allowedRoles={["system_admin"]}>
              <UserManager />
            </ProtectedRoute>
          } />

          <Route path="/admin/vessel-calendar-diff" element={
            <ProtectedRoute allowedRoles={["system_admin"]}>
              <VesselMovementDiff />
            </ProtectedRoute>
          } />

          <Route path="/voyage-plan" element={
            <ProtectedRoute allowedRoles={["pilot", "system_admin"]} allowedPositions={["slip_coordinator"]}>
              <VoyagePlan />
            </ProtectedRoute>
          } />

        </Route> {/* End Layout Wrapper */}

        {/* Catch All — an unknown path must CLEAR the resume pointer before
            leaving, or the app orbits forever: "/" resumes to the saved path,
            the catch-all bounces away, "/" resumes again... until Safari
            kills it ("history.replaceState more than 100 times per 10
            seconds") and the pilot is stuck on the error screen on every
            launch, restarts included (Aaron, 2026-09-01: a spreadsheet
            export's blob id got recorded as his last screen, path=/9430d02b-
            ...). Same hole DeniedHome already plugs for permission denials.
            Goes home, not to /login: RootRedirect sends a signed-out visitor
            to login anyway, and a signed-in one belongs on their own page. */}
        <Route path="*" element={<UnknownRouteHome />} />

      </Routes>
      </Suspense>
      </ChunkErrorBoundary>
      {/* Outside the boundary on purpose: feedback stays reachable even when a
          screen fails to load. Renders only when signed in. */}
      {session && <FeedbackWidget />}
    </div >
  );
}